import IconColumns from '@assets/icons/columns.svg?react';
import IconFilters from '@assets/icons/filters.svg?react';
import IconInfo from '@assets/icons/info.svg?react';
import IconMoon from '@assets/icons/moon.svg?react';
import GuideStep1Image from '@assets/guide/guide-1.png';
import GuideStep2Image from '@assets/guide/guide-2.png';
import GuideStep3Image from '@assets/guide/guide-3.png';
import GuideStep4Image from '@assets/guide/guide-4.png';
import {
 Box,
 Button,
 Dialog,
 DialogContent,
 DialogTitle,
 MobileStepper,
 Typography,
 useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseButton from '../CloseButton';

type GuideStep = {
 icon: React.ReactNode;
 titleKey: string;
 paragraphs: string[];
 imageSrc: string;
 imageSrc2x: string;
};

const UserGuideModal = ({
 isOpen,
 onClose,
}: {
 isOpen: boolean;
 onClose: () => void;
}) => {
 const { t } = useTranslation();
 const theme = useTheme();
 const [activeStep, setActiveStep] = useState(0);

 const steps: GuideStep[] = [
  {
   icon: (
    <IconColumns
     style={{ width: 24, height: 24, color: theme.palette.kemek.blue }}
    />
   ),
   titleKey: 'userGuide.step1.title',
   paragraphs: [
    'userGuide.step1.paragraph1',
    'userGuide.step1.paragraph2',
    'userGuide.step1.paragraph3',
   ],
   imageSrc: GuideStep1Image,
   imageSrc2x: GuideStep1Image,
  },
  {
   icon: (
    <IconFilters
     style={{ width: 24, height: 24, color: theme.palette.kemek.blue }}
    />
   ),
   titleKey: 'userGuide.step2.title',
   paragraphs: [
    'userGuide.step2.paragraph1',
    'userGuide.step2.paragraph2',
    'userGuide.step2.paragraph3',
   ],
   imageSrc: GuideStep2Image,
   imageSrc2x: GuideStep2Image,
  },
  {
   icon: (
    <IconInfo
     style={{ width: 24, height: 24, color: theme.palette.kemek.blue }}
    />
   ),
   titleKey: 'userGuide.step3.title',
   paragraphs: [
    'userGuide.step3.paragraph1',
    'userGuide.step3.paragraph2',
    'userGuide.step3.paragraph3',
   ],
   imageSrc: GuideStep3Image,
   imageSrc2x: GuideStep3Image,
  },
  {
   icon: (
    <IconMoon
     style={{ width: 24, height: 24, color: theme.palette.kemek.blue }}
    />
   ),
   titleKey: 'userGuide.step4.title',
   paragraphs: [
    'userGuide.step4.paragraph1',
    'userGuide.step4.paragraph2',
    'userGuide.step4.paragraph3',
   ],
   imageSrc: GuideStep4Image,
   imageSrc2x: GuideStep4Image,
  },
 ];

 const currentStep = steps[activeStep];
 const isLastStep = activeStep === steps.length - 1;

 const handleNext = () => {
  if (isLastStep) {
   onClose();
   setActiveStep(0);
   return;
  }
  setActiveStep((prev) => prev + 1);
 };

 const handleBack = () => {
  setActiveStep((prev) => prev - 1);
 };

 const handleClose = () => {
  onClose();
  setActiveStep(0);
 };

 return (
  <Dialog
   open={isOpen}
   onClose={handleClose}
   fullWidth
   sx={{
    '& .MuiDialog-container': {
     '& .MuiPaper-root': {
      width: '100%',
      maxWidth: '730px',
     },
    },
   }}
  >
   <DialogTitle sx={{ pr: 6 }}>{t('userGuide.title')}</DialogTitle>
   <CloseButton closeDialog={handleClose} />

   <DialogContent sx={{ pt: 1, pb: 0, px: 3 }}>
    <Box
     component="img"
     src={currentStep.imageSrc}
     srcSet={`${currentStep.imageSrc} 1x, ${currentStep.imageSrc2x} 2x`}
     alt={t(currentStep.titleKey)}
     sx={{
      width: '100%',
      //   height: 260,
      //   borderRadius: 2,
      backgroundColor: theme.palette.kemek.bg1,
      //   objectFit: 'cover',
      display: 'block',
      aspectRatio: '676/260',
      mb: '28px',
     }}
    />

    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
     <Box
      sx={{
       width: 32,
       height: 32,
       borderRadius: '50%',
       backgroundColor: theme.palette.kemek.lightBlue,
       display: 'flex',
       alignItems: 'center',
       justifyContent: 'center',
      }}
     >
      {currentStep.icon}
     </Box>
     <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
      {t(currentStep.titleKey)}
     </Typography>
    </Box>

    <Box
     sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: '12px' }}
    >
     {currentStep.paragraphs.map((key) => (
      <Typography
       key={key}
       sx={{
        fontSize: 14,
        color: theme.palette.kemek.darkGray,
        lineHeight: 1.6,
       }}
      >
       {t(key)}
      </Typography>
     ))}
    </Box>
   </DialogContent>

   <MobileStepper
    variant="dots"
    steps={steps.length}
    position="static"
    activeStep={activeStep}
    elevation={0}
    sx={{
     px: 3,
     py: 2,
     backgroundColor: 'transparent',
     '& .MuiMobileStepper-dotActive': {
      backgroundColor: theme.palette.kemek.blue,
     },
    }}
    nextButton={
     <Button
      size="small"
      variant="contained"
      onClick={handleNext}
      aria-label={isLastStep ? t('userGuide.finish') : t('userGuide.next')}
     >
      {isLastStep ? t('userGuide.finish') : t('userGuide.next')}
     </Button>
    }
    backButton={
     <Button
      size="small"
      variant="text"
      color="secondary"
      onClick={handleBack}
      disabled={activeStep === 0}
      aria-label={t('userGuide.back')}
      sx={{
       height: '40px',
      }}
     >
      {t('userGuide.back')}
     </Button>
    }
   />
  </Dialog>
 );
};

export default UserGuideModal;
